import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Modal from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import EnrollPictureScreen from '../../components/EnrollPictureScreen/EnrollPictureScreen';
import { useToast } from '../../components/Toast/ToastContext';
import { rescueEnrollDevicesProgramMutation } from '../../lib/rescueQueries';
import devicesHero from '../../assets/devices-hero.jpg';
import devicesTable from '../../assets/devices-table.png';
import devicesStarterBatch from '../../assets/devices-starter-batch.jpg';
import sharedStyles from '../../components/EnrollPictureScreen/EnrollPictureScreen.module.scss';
import styles from './DevicesEnrollModal.module.scss';

interface DevicesEnrollModalProps {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

export default function DevicesEnrollModal({ open, onClose, onEnrolled }: DevicesEnrollModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const { showToast } = useToast();
  const [enrollDevicesProgram, { loading }] = useMutation(rescueEnrollDevicesProgramMutation);

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  const handleEnroll = async () => {
    try {
      await enrollDevicesProgram();
      showToast('You’re enrolled in the Fi devices program');
      onEnrolled();
      setStep(1);
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
      showToast(`Failed to enroll: ${msg}`);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="extra-wide"
      className={sharedStyles.modal}
      backdropClassName={sharedStyles.backdrop}
      showCloseX={false}
    >
      {step === 1 && (
        <EnrollPictureScreen
          heroSrc={devicesHero}
          onClose={handleClose}
          title="Add GPS tracking to your rescue program"
          description="Track foster dogs in real time and give adopters 6 months free when they adopt. Manage your whole fleet from the portal."
          currentStep={1}
          totalSteps={3}
          actions={
            <Button variant="dark" onClick={() => setStep(2)}>
              Next &rarr;
            </Button>
          }
        />
      )}

      {step === 2 && (
        <EnrollPictureScreen
          heroSrc={devicesTable}
          onClose={handleClose}
          title="Here's how it works"
          description="Manage battery, status and safe zones for every device. Transfer to fosters, initiate adoptions, and send reminders — all from the portal."
          currentStep={2}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <>
              <Button variant="secondary" onClick={() => setStep(1)}>
                &larr; Back
              </Button>
              <Button variant="dark" onClick={() => setStep(3)}>
                Next &rarr;
              </Button>
            </>
          }
        />
      )}

      {step === 3 && (
        <EnrollPictureScreen
          heroSrc={devicesStarterBatch}
          heroClassName={styles.starterBatchHero}
          onClose={handleClose}
          title="Your first order is 10 Fi Minis"
          description="Put one on each of your next 10 dogs as they're adopted, then complete a Transfer to Adopter. Hit a 70% transfer rate and we'll send more to protect even more dogs in your care."
          currentStep={3}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <>
              <Button variant="secondary" onClick={() => setStep(2)} disabled={loading}>
                &larr; Back
              </Button>
              <Button variant="dark" onClick={handleEnroll} disabled={loading}>
                {loading ? 'Enrolling…' : 'Enroll'}
              </Button>
            </>
          }
        />
      )}
    </Modal>
  );
}
